import { FC, RefObject, useCallback, useMemo, useRef, useState } from 'react';
import { Button, Flex } from 'antd';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { Dayjs } from 'dayjs';
import { DataObjectDescriptionVersionStore } from '../DataObjectDescriptionVersionStore';
import { useSystemClassifierItemsBy } from '../../configuration';
import { Frequency } from '../types';
import { getDayjs, ISODate } from '../../common/utils';
import {
  ActionButton,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Padding,
} from '../../common/components';
import {
  confirmPanelFormClose,
  Form,
  FormAlerts,
  FormField,
  FormRef,
  useAlertContext,
} from '../../common/form';
import { ValidateRequiredKeysFormSchema } from '../schema/ValidateRequiredKeysFormSchema';
import { useFormState } from 'react-hook-form';
import { isDisabledDate } from '../valueDate/utils';
import translations from './ValidateRequiredKeys.translations.json';
import validationTranslations from '../translations/validation.translations.json';
import { FormPeriodEndPicker } from '../../common/form/input/FormPeriodEndPicker';
import { ValidateRequiredKeysDataType } from '../schema';
import {
  useValidateRequiredKeys,
  ValidationErrorsExcel,
} from '../query/useValidateRequiredKeys';
import { ValidationMessagesMap } from '../../common/utils/ValidationMessagesMap';
import styled from 'styled-components';
import { useQueryClient } from '@tanstack/react-query';
import { AlertsPanel } from '../../common/form/alerts/AlertsPanel';
import { useDataObjectContext } from '../DataObjectContext';
import { base64ToBlob, downloadFile } from '../../common/utils/file';
import { translateProcessingErrorCode } from '../../processingEvent/processingErrorMessage';

const { addTranslations, t } = i18nNsFactory('validate-keys-modal');
addTranslations(translations);
addTranslations(validationTranslations);

export interface Props {
  isModalOpen: boolean;
  onModalClose: () => void;
}

export const ValidateRequiredKeys: FC<Props> = (props) => {
  const { isModalOpen, onModalClose } = props;
  const [valueDate, setValueDate] = useState<ISODate | null>(null);
  const [dataObjectDescriptionVersion] =
    DataObjectDescriptionVersionStore.useValue('dataObjectDescriptionVersion');
  const { data, isFetching } = useValidateRequiredKeys(
    { id: dataObjectDescriptionVersion.versionId, valueDate },
    { enabled: !!dataObjectDescriptionVersion.versionId && !!valueDate },
  );
  const queryClient = useQueryClient();
  const { resetAlert, setSuccess } = useAlertContext();
  const { setValidateKeysModalOpen, setFocusedKeyId } = useDataObjectContext();
  const [isErrorDrawerOpen, setIsErrorDrawerOpen] = useState(false);
  const formRef: RefObject<FormRef<any> | null> = useRef(null);

  const onSubmit = useCallback(
    ({ valueDate }: ValidateRequiredKeysDataType) => {
      setValueDate(valueDate);

      formRef?.current?.form.reset(
        { valueDate: undefined },
        {
          keepValues: false,
          keepDirty: false,
        },
      );
      formRef?.current?.supressBlock();
      onModalClose();
    },
    [onModalClose, formRef],
  );

  const downloadErrorsExcel = (excel: ValidationErrorsExcel) => {
    const blob = base64ToBlob(excel.base64, excel.contentType);
    downloadFile(blob, excel.fileName);
  };

  const systemClassifierItemsById = useSystemClassifierItemsBy(['FREQUENCY']);
  const frequencyCode = (systemClassifierItemsById['FREQUENCY'].get(
    dataObjectDescriptionVersion?.frequencyId ?? 0,
  )?.code ?? 'WEEK') as Frequency;

  const validPeriod = [
    getDayjs(dataObjectDescriptionVersion?.validPeriod[0]),
    getDayjs(dataObjectDescriptionVersion?.validPeriod[1]),
  ] as [Dayjs | null, Dayjs | null];

  const validateKeysErrorsMap: ValidationMessagesMap | null = useMemo(() => {
    resetAlert();
    if (data?.errors) {
      if (data.errors.length === 0) setSuccess(t('alert.success'));
      const messagesMap = new ValidationMessagesMap();
      for (const row of data.errors) {
        messagesMap.append(
          `${row.id}-${row.sourceKey}-${row.error}`,
          translateProcessingErrorCode(row.error, row.args) ?? row.error,
          'error',
        );
      }
      return messagesMap;
    } else return null;
  }, [data?.errors, resetAlert, setSuccess]);

  const uniqueErrorKeysCount = useMemo(() => {
    const ids = data?.errors?.map((e) => e.sourceKey) ?? [];
    return new Set(ids).size;
  }, [data?.errors]);

  return (
    <Form
      schema={ValidateRequiredKeysFormSchema}
      onSubmit={onSubmit}
      idAttributePrefix="validateKeys"
      defaultValues={{ valueDate: undefined }}
      formRef={formRef}
      blockNavigationOnFirstChange={false}
    >
      {validateKeysErrorsMap && (
        <AlertsPanel
          validationMessagesMap={validateKeysErrorsMap}
          message={t('alert.message', { count: uniqueErrorKeysCount })}
          renderErrorMeta={(name, _message, index) => {
            return (
              name &&
              name !== 'root' && (
                <ErrorButton
                  id={`alerts-panel-errors-${index}`}
                  size="small"
                  onClick={() => {
                    setValidateKeysModalOpen(false);
                    setTimeout(() => {
                      setFocusedKeyId(data?.errors?.[index]?.id);
                    }, 300);
                  }}
                >
                  {t('button.goto')}
                </ErrorButton>
              )
            );
          }}
          action={
            data?.errors?.length && data?.errors?.length > 0 ? (
              <Button
                id="dowload-keys-excel"
                onClick={() =>
                  downloadErrorsExcel(data.excel as ValidationErrorsExcel)
                }
                type="default"
                size="middle"
              >
                {t('alert.download')}
              </Button>
            ) : undefined
          }
          closable
          onClose={() => {
            queryClient.removeQueries({
              queryKey: ['data-object-description-validate-required-keys'],
              exact: false,
            });
            setValueDate(null);
          }}
          isErrorDrawerOpen={isErrorDrawerOpen}
          setIsErrorDrawerOpen={setIsErrorDrawerOpen}
        />
      )}
      {isModalOpen && (
        <ValidateRequiredKeysModal
          validPeriod={validPeriod}
          frequency={frequencyCode}
          onClose={onModalClose}
          isFetching={isFetching}
        />
      )}
    </Form>
  );
};

function ValidateRequiredKeysModal({
  validPeriod,
  frequency,
  onClose,
  isFetching,
}: {
  validPeriod: [Dayjs | null, Dayjs | null];
  frequency: Frequency;
  onClose: () => void;
  isFetching: boolean;
}) {
  const { isDirty } = useFormState();
  const handleCancel = useCallback(() => {
    if (isDirty) {
      confirmPanelFormClose(onClose);
    } else {
      onClose?.();
    }
  }, [isDirty, onClose]);

  return (
    <Modal
      footer={
        <ModalFooter>
          <Flex justify="end" gap="small">
            <ActionButton
              type="abort"
              onClick={handleCancel}
              label={t('button.cancel')}
              disabled={isFetching}
            />
            <ActionButton
              type="formSubmit"
              label={t('button.check')}
              loading={isFetching}
            />
          </Flex>
        </ModalFooter>
      }
      onCancel={isFetching ? void 0 : onClose}
      width="30rem"
    >
      <ModalHeader>
        <ModalTitle>{t(`heading`)}</ModalTitle>
      </ModalHeader>
      <FormAlerts />
      <Padding scrollable>
        <Flex vertical gap="middle">
          <FormField
            name="valueDate"
            size="small"
            label={t('fields.valueDate.label')}
            required
          >
            <FormPeriodEndPicker
              name="valueDate"
              frequency={frequency}
              disabledDate={(date: Dayjs) => {
                const correctedDate =
                  frequency === 'MONTH'
                    ? date.endOf('month').startOf('day')
                    : date;
                return isDisabledDate(correctedDate, [validPeriod]);
              }}
            />
          </FormField>
        </Flex>
      </Padding>
    </Modal>
  );
}

const ErrorButton = styled(Button)`
  align-self: flex-start;
`;
